import { Injectable } from '@angular/core';

export interface UserSession {
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {
  private readonly USER_KEY = 'current_user';
  private readonly TOKEN_EXPIRY_KEY = 'token_expires_at';
  private readonly ACCESS_TOKEN_KEY = 'access_token';

  // Храним JWT токен в памяти и localStorage для восстановления после перезагрузки и между вкладками
  private accessToken: string | null = null;
  private tokenExpiry: Date | null = null;

  constructor() {
    // Восстанавливаем токен, пользователя и время истечения из localStorage
    this.loadPersistedData();
  }

  // JWT токен хранится в памяти и localStorage для восстановления
  setToken(token: string): void {
    this.accessToken = token;
    // Сохраняем в localStorage для восстановления после перезагрузки и между вкладками
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  getToken(): string | null {
    // Если токена нет в памяти, пробуем восстановить из localStorage
    if (!this.accessToken) {
      this.accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    }

    // Проверяем, не истек ли токен
    if (this.accessToken && this.isTokenExpired()) {
      this.clearAccessToken();
      return null;
    }
    return this.accessToken;
  }

  // Refresh токен хранится в HttpOnly cookie, не в sessionStorage
  setRefreshToken(token: string): void {
    // Этот метод больше не нужен - refresh token управляется бэкендом через HttpOnly cookie
    console.warn('Refresh token теперь управляется через HttpOnly cookie');
  }

  getRefreshToken(): string | null {
    // Refresh token теперь передается через HttpOnly cookie
    return null;
  }

  setUser(user: UserSession): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  getUser(): UserSession | null {
    const user = localStorage.getItem(this.USER_KEY);
    if (user) {
      try {
        return JSON.parse(user) as UserSession;
      } catch {
        this.clear();
        return null;
      }
    }
    return null;
  }

  setTokenExpiry(expiresAt: string): void {
    this.tokenExpiry = new Date(expiresAt);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiresAt);
  }

  getTokenExpiry(): string | null {
    return localStorage.getItem(this.TOKEN_EXPIRY_KEY);
  }

  isTokenExpired(): boolean {
    if (this.tokenExpiry) {
      if (Number.isNaN(this.tokenExpiry.getTime())) return true;
      return new Date() >= this.tokenExpiry;
    }

    // Fallback к sessionStorage
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;

    const expiryDate = new Date(expiry);
    if (Number.isNaN(expiryDate.getTime())) return true;

    const now = new Date();
    return now >= expiryDate;
  }

  clear(): void {
    this.clearAccessToken();
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
  }

  private clearAccessToken(): void {
    this.accessToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
  }

  private loadPersistedData(): void {
    // Восстанавливаем токен из localStorage
    this.accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);

    // Восстанавливаем время истечения токена
    const expiry = this.getTokenExpiry();
    if (expiry) {
      this.tokenExpiry = new Date(expiry);
    }
  }

  // Проверка валидности токена
  isValidToken(): boolean {
    const token = this.getToken();
    return token !== null && !this.isTokenExpired();
  }

  // Метод для обновления токена при refresh
  refreshToken(newToken: string, expiresAt: string): void {
    this.setToken(newToken);
    this.setTokenExpiry(expiresAt);
  }
}
